import React from "react";
import { siteData } from "../data/mockData";
import { Award, ShieldCheck, Microscope, Users } from "lucide-react";

const statIcons = [Users, Award, Award, ShieldCheck];

const highlightIcons = [Microscope, ShieldCheck, Users];

const HistorySection = () => {
  const { history } = siteData;

  return (
    <section
      id="about"
      className="w-full bg-[#FAFAFA] py-16 md:py-24 px-6 md:px-16"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Header - consistent style */}
        <div className="mb-14">
          <div className="flex flex-col md:flex-row items-start md:items-start justify-between">
            <h2 className="font-poppins font-bold text-[2rem] md:text-[2.5rem] text-[#1a1a1a] leading-tight">
              {history.sectionTitle1}
            </h2>
            <div className="flex flex-col items-end mt-2 md:mt-0">
              <h3 className="font-poppins font-bold text-[1.8rem] md:text-[2.2rem] text-[#1a1a1a] italic text-right">
                {history.sectionTitle2}
              </h3>
              <h3 className="font-poppins font-bold text-[1.8rem] md:text-[2.2rem] text-[#D1D776] italic text-right">
                {history.sectionHighlight}
              </h3>
            </div>
          </div>
          <div className="w-full h-[3px] bg-[#D1D776] mt-2" />
        </div>

        {/* Hero: Image + Intro Story */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 mb-16">
          {/* Left - Image with teal accent + stat overlay */}
          <div className="lg:w-[45%] relative">
            <div className="relative">
              <div className="absolute top-0 left-0 w-[6px] h-full bg-[#1CA0B9] rounded-full" />
              <img
                src={history.image}
                alt="Happy patient at Anish Dental"
                className="w-full h-auto object-cover rounded-xl ml-4 shadow-md"
              />
              {/* Floating stat card on image */}
              <div className="absolute -bottom-6 -right-4 md:right-4 bg-white rounded-xl shadow-lg px-5 py-4 border border-gray-100">
                <p className="font-poppins font-extrabold text-[#1CA0B9] text-3xl leading-none">
                  25+
                </p>
                <p className="font-poppins text-[#555] text-xs mt-1">
                  Years of Service
                </p>
              </div>
            </div>
          </div>

          {/* Right - Story + Surgeon */}
          <div className="lg:w-[55%] flex flex-col justify-center">
            <p className="font-poppins text-[#444] text-[15px] md:text-base leading-relaxed mb-8">
              {history.intro}
            </p>

            {/* Surgeon highlight */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#1CA0B9]/10 flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-[#1CA0B9]" />
                </div>
                <h4 className="font-poppins font-bold text-[#1a1a1a] text-lg">
                  {history.surgeon.title}
                </h4>
              </div>
              <p className="font-poppins text-[#555] text-sm leading-relaxed">
                {history.surgeon.text}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {history.stats.map((stat, index) => {
            const Icon = statIcons[index];
            return (
              <div
                key={index}
                className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm text-center group hover:border-[#1CA0B9]/30 hover:shadow-md transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-full bg-[#1CA0B9]/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-[#1CA0B9]/20 transition-colors">
                  <Icon className="w-5 h-5 text-[#1CA0B9]" />
                </div>
                <p className="font-poppins font-extrabold text-[#1CA0B9] text-2xl md:text-3xl leading-none">
                  {stat.value}
                </p>
                <p className="font-poppins text-[#777] text-xs md:text-sm mt-1.5">
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Highlight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {history.highlights.map((item, index) => {
            const Icon = highlightIcons[index];
            return (
              <div
                key={index}
                className="relative bg-white rounded-xl p-6 md:p-7 border border-gray-100 shadow-sm group hover:shadow-md hover:border-[#1CA0B9]/20 transition-all duration-300"
              >
                {/* Top accent line */}
                <div className="absolute top-0 left-6 right-6 h-[3px] bg-[#1CA0B9] rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-[#D1D776]/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4.5 h-4.5 text-[#D1D776]" strokeWidth={2} />
                  </div>
                  <h4 className="font-poppins font-bold text-[#1a1a1a] text-base">
                    {item.title}
                  </h4>
                </div>
                <p className="font-poppins text-[#666] text-sm leading-relaxed">
                  {item.text}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HistorySection;
